<template>
  <content-subpage
    class="lesson-forum-topic-resource-create-subpage"
    :visible="visible"
    :has-parent-subpage="!!hasParentSubpage"
    :titles="titles"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <cv-form class="create-review" @submit.prevent>
        <dl class="field-set info">
          <dt class="field-label">
            {{ t("teacher_reviews_subpage_create_review_message_label") }}
          </dt>
          <dd class="field-value">
            <rich-text-editor
              ref="bodyRichTextEditor"
              class="body"
              kind="teacher_tinymce_4"
              size="large"
              :is-mandatory="true"
              :initial-content="review"
              :disabled="isInputDisabled"
              :text-id="dirtyId"
              @change="handleReviewChange"
            />
          </dd>
        </dl>
      </cv-form>
    </template>
    <template #action-button>
      <fetch-data-button
        class="save"
        v-bind="dataSaveButton"
        kind="primary"
        :disabled="actionButtonDisabled"
        @click="handleSaveReview"
        @reset-completed-state="handleSaveButtonResetCompleted"
        @reset-error-state="handleSaveButtonResetError"
      >
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("teacher_reviews_subpage_create_review_create_button") }}</span>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import LessonReviewApi from "@apis/lesson.review.topic.api";
import {
  FORUM_TOPIC_RESOURCE_TYPE,
  RESOURCE_TYPE,
} from "@modules/lesson-review/lesson.topic.constant.ts";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import RichTextEditor from "@plugins/richtext/components/RichTextEditor.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { ForumTopicModel } from "@modules/lesson-review/lesson-review-types.ts";
import { ResourceStatus } from "@/types/lesson.ts";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";

// Props typing
interface PropsParamType {
  uuid: string;
  status?: ResourceStatus;
  baseTitle: string[];
  topic: ForumTopicModel;
  titles: string[];
  addResourceActionBack: () => void;
  actionBack: () => void;
}

interface Props {
  params: PropsParamType;
}
const props = defineProps<Props>();

// State
const review = ref<string>("");
const visible = ref<boolean>(true);
const parentSubpageName = ref<string>("");
const paramsData = ref<PropsParamType | null>(props.params ? { ...props.params } : null);
const bodyRichTextEditor = ref();

// Computed
const dirtyId = computed<string>(() => (paramsData.value ? paramsData.value.uuid : ""));

// contentSubpage
const {
  titles,
  setDirty,
  removeDirty,
  dataSaveButton,
  hasParentSubpage,
  actionHidden,
  actionBack,
  defaultExpose,
} = useContentSubpage({ id: dirtyId });

dataSaveButton.value = {
  kind: "primary",
  size: "field",
  loading: false,
  error: false,
  completed: false,
  tipPosition: "bottom",
  tipAlignment: "end",
  errorMessage: "",
};

const isInputDisabled = computed<boolean>(
  () => dataSaveButton.value.loading || dataSaveButton.value.completed,
);
const actionButtonDisabled = computed<boolean>(
  () => dataSaveButton.value.loading || dataSaveButton.value.completed,
);

// i18n
const { t } = useI18n();

const errorHandler = useErrorHandler();

// Methods
const handleReviewChange = function (newValue) {
  setDirty(dirtyId.value);
  review.value = newValue;
};

const validate = function () {
  return bodyRichTextEditor.value.validate();
};

const saveForm = function () {
  const postModel = {
    type: props.params.topic.type
      ? props.params.topic.type
      : FORUM_TOPIC_RESOURCE_TYPE.LESSON_TEACHER_REVIEW,
    topicId: props.params.topic.id,
    rt_content: review.value,
    resourceId: 0,
    forumTopicResourceUuid: null,
    resourceType: RESOURCE_TYPE.POST,
  };
  return new Promise(() => {
    LessonReviewApi.createLessonTeacherReviewTopicResource(postModel).then((response) => {
      if (response.code !== "ok") {
        errorHandler.passiveApiFailHandler();
        handleSaveButtonResetError();
      }
      removeDirty(dirtyId.value);
      dataSaveButton.value.loading = false;
      dataSaveButton.value.completed = true;
      actionBack();
      props.params.addResourceActionBack();
    }, errorHandler.passiveApiFailHandler);
  });
};

const handleSaveReview = function () {
  dataSaveButton.value.loading = true;
  if (validate()) {
    saveForm();
  } else {
    handleSaveButtonError();
  }
};

const handleSaveButtonError = function () {
  dataSaveButton.value.loading = false;
  dataSaveButton.value.error = true;
};

const handleSaveButtonResetError = function () {
  dataSaveButton.value.loading = false;
  dataSaveButton.value.error = false;
};

const handleSaveButtonResetCompleted = function () {
  // TODO Cannot find postSuccessStateAction in anywhere, need to confirm
  // if (this.postSuccessStateAction && typeof this.postSuccessStateAction === "function") {
  //   this.postSuccessStateAction();
  // }
  dataSaveButton.value.loading = false;
  dataSaveButton.value.completed = false;
};

// Created
parentSubpageName.value = props.params?.titles?.slice(-1)[0] ?? "";
titles.value.push(t("teacher_reviews_subpage_create_review_title"));

defineExpose(defaultExpose);
</script>
